// @flow

import React from 'react';

import { zoomTableActionsConfig } from '../../commons/zoomTable/zoomTableConfig';
import { RenderDepositSlipTableActionButtons } from '../../commons/tableActionButton/tableActionButton';

import {
  getRetailTableCellObject,
  getFormattedDate,
  getFormattedTime,
  strUsables
} from '../../../services/helpers';

const checkmarkCircle = require('../../../assets/images/checkmark-circle.svg');

export const retailCashbookHandoverTransactionConfig = {
  columns: [
    {
      Header: 'CN No.',
      headerClassName: 'flex-20',
      className: 'table-cell-custom-retail flex-20',
      accessor: 'cnote'
    },
    {
      Header: 'Collection Type',
      headerClassName: 'flex-25',
      className: 'table-cell-custom-retail flex-25',
      accessor: 'paymentMode'
    },
    {
      Header: 'Payment Type',
      headerClassName: 'flex-35',
      className: 'table-cell-custom-retail flex-35',
      Cell: (row: Object) =>
        getRetailTableCellObject(
          `${row.original.paymentType}${
            row.original.bankName ? ` | ${row.original.bankName}` : ''
          }`,
          `${
            row.original.chequeNumber
              ? `Cheque No. ${row.original.chequeNumber}`
              : ''
          }`
        )
    },
    {
      Header: 'Amount',
      headerClassName: 'flex-20',
      className: 'table-cell-custom-retail flex-20',
      Cell: (row: Object) => (
        <div>₹{Math.round(row.original.amount * 100) / 100}</div>
      ),
      sortMethod: (a, b) => (a > b ? 1 : -1)
    }
  ],
  className: 'zoomTableLegacyWrapper',
  noDataText: 'No handovers found',
  minRows: 7,
  pageSizeOptions: [7, 10, 15, 25, 50, 100],
  style: { maxHeight: '35rem' }
};

export const retailCashbookTableConfig = {
  columns: [
    {
      Header: 'Date-Time',
      headerClassName: 'flex-20',
      className: 'table-cell-custom-retail flex-20',
      accessor: 'transactionTime',
      formatter: (row: Object) =>
        row && new Date(row.transactionTime).toDateString(),
      Cell: (row: Object) =>
        getRetailTableCellObject(
          getFormattedDate(row.original.transactionTime),
          getFormattedTime(row.original.transactionTime)
        ),
      sortMethod: (a, b) => (a > b ? 1 : -1)
    },
    {
      Header: 'Remarks',
      headerClassName: 'flex-30',
      className: 'table-cell-custom-retail flex-30',
      accessor: 'remarks',
      Cell: (row: Object) =>
        getRetailTableCellObject(row.original.remarks, ' '),
      sortMethod: (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
    },
    {
      Header: 'Recieved From',
      headerClassName: 'flex-20',
      className: 'table-cell-custom-retail flex-20',
      accessor: 'recievedFromName',
      Cell: (row: Object) =>
        getRetailTableCellObject(
          row.original.recievedFromName,
          row.original.recievedFromMobile
        ),
      sortMethod: (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
    },
    {
      Header: 'Recieved By',
      headerClassName: 'flex-30',
      className: 'table-cell-custom-retail flex-30',
      accessor: 'recievedByName',
      Cell: (row: Object) =>
        getRetailTableCellObject(
          row.original.recievedByName,
          row.original.recievedByMobile
        ),
      sortMethod: (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
    },
    {
      Header: 'Transaction ID',
      headerClassName: 'flex-30',
      className: 'table-cell-custom-retail-clickable flex-30',
      accessor: 'transactionId',
      Cell: (row: Object) => (
        <div
          onClick={() => {
            zoomTableActionsConfig.RTL_CASHBOOK.actions.openHandoverTransactionModal(
              row
            );
          }}
        >
          {row.original.transactionId}
        </div>
      ),
      sortMethod: (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
    },
    {
      Header: 'Debit',
      headerClassName: 'flex-30',
      className: 'table-cell-custom-retail flex-30',
      sortable: false,
      formatter: (row: Object) =>
        row.transactionType === 'DEBIT'
          ? Math.round(row.totalAmount * 100) / 100
          : '--',
      Cell: (row: Object) => (
        <div>
          {row.original.transactionType === 'DEBIT'
            ? `₹${Math.round(row.original.totalAmount * 100) / 100}`
            : '--'}
        </div>
      )
    },
    {
      Header: 'Credit',
      headerClassName: 'flex-20',
      className: 'table-cell-custom-retail flex-20',
      sortable: false,
      formatter: (row: Object) =>
        row.transactionType === 'CREDIT'
          ? Math.round(row.totalAmount * 100) / 100
          : '--',
      Cell: (row: Object) => (
        <div>
          {row.original.transactionType === 'CREDIT'
            ? `₹${Math.round(row.original.totalAmount * 100) / 100}`
            : '--'}
        </div>
      )
    },
    {
      Header: 'Outstanding',
      headerClassName: 'flex-30',
      className: 'table-cell-custom-retail flex-30',
      accessor: 'outstanding',
      Cell: (row: Object) => (
        <div>₹{Math.round(row.original.outstanding * 100) / 100}</div>
      ),
      sortMethod: (a, b) => (a > b ? 1 : -1)
    }
  ],
  className: 'zoomTableLegacyWrapper',
  noDataText: 'No data found',
  minRows: 7,
  pageSizeOptions: [7, 10, 15, 25, 50, 100],
  style: { maxHeight: 'calc(100vh - 21rem)' }
};

const compareTransactionTime = (a: Object, b: Object) => {
  if (a.transactionTime < b.transactionTime) {
    return -1;
  }
  if (a.transactionTime > b.transactionTime) {
    return 1;
  }
  return 0;
};

export const getOutstandingAmounts = (tableData: Object) => {
  if (tableData && tableData.length > 0) {
    const tableDataSorted = tableData.sort(compareTransactionTime);
    const outstandingAmounts = {
      openingOutstanding: tableDataSorted[0].openingOutstanding,
      closingOutstanding:
        tableDataSorted[tableDataSorted.length - 1].closingOutstanding
    };
    return outstandingAmounts;
  }
  return null;
};

const depositSlipTableHeaderStyle = {
  opacity: '1',
  fontWeight: 'bolder'
};

export const depositSlipButtonConfig = {
  buttons: [
    {
      iconRef: checkmarkCircle,
      iconContainerStyle: {
        display: 'flex',
        cursor: 'pointer',
        width: '100%',
        padding: '.2rem',
        height: '70%',
        opacity: '1'
      },
      onClick: rowData => {
        if (
          zoomTableActionsConfig.DEPOSIT_SLIP_CASH &&
          zoomTableActionsConfig.DEPOSIT_SLIP_CASH.actions &&
          zoomTableActionsConfig.DEPOSIT_SLIP_CASH.actions.toggleRowRemoval
        ) {
          zoomTableActionsConfig.DEPOSIT_SLIP_CASH.actions.toggleRowRemoval(
            rowData
          );
        }
      },
      style: {
        // Camel case all your known styles. x% width equals x% of 140px.
        width: '20%',
        height: '50%',
        margin: '5%',
        opacity: '1'
      }
    }
  ]
};

const renderActionButtons = (row: Object) => (
  <RenderDepositSlipTableActionButtons
    buttonConfig={depositSlipButtonConfig.buttons}
    rowData={row.original || {}}
  />
);

export const depositSlipCashTableConfig = {
  columns: [
    {
      Header: '',
      headerStyle: depositSlipTableHeaderStyle,
      accessor: 'checked',
      Cell: (row: Object) => renderActionButtons(row),
      style: { opacity: '1' }
    },
    {
      Header: 'CN',
      headerStyle: depositSlipTableHeaderStyle,
      accessor: 'cnote',
      Cell: (row: Object) => (
        <div>
          <span className="text-wrap-ellipsis" title={row.original.cnote}>
            {row.original.cnote}
          </span>
          &nbsp;
          {row.original.additionalNumberOfCnotes ? (
            <span
              className="text-wrap-ellipsis"
              title={row.original.additionalNumberOfCnotes}
              style={{ color: '#dd712f' }}
            >
              (+{row.original.additionalNumberOfCnotes})
            </span>
          ) : null}
          <div className="text-wrap-ellipsis" title={row.original.paymentMode}>
            {row.original.paymentMode}
          </div>
        </div>
      )
    },
    {
      Header: 'Instruments',
      headerStyle: depositSlipTableHeaderStyle,
      accessor: 'instrument',
      Cell: (row: Object) => (
        <div className="text-wrap-ellipsis" title={row.original.instrument}>
          {row.original.instrument}
        </div>
      )
    },
    {
      Header: 'CollectedBy',
      headerStyle: depositSlipTableHeaderStyle,
      accessor: 'collectedBy',
      Cell: (row: Object) => (
        <div className="text-wrap-ellipsis" title={row.original.collectedBy}>
          {row.original.collectedBy}
        </div>
      )
    },
    {
      Header: 'Amount',
      headerStyle: depositSlipTableHeaderStyle,
      accessor: 'amount',
      Cell: (row: Object) => (
        <div className="text-wrap-ellipsis" title={row.original.amount}>
          {strUsables.indianCurrencySymol} {row.original.amount}
        </div>
      )
    }
  ],
  className: `deposit-slip-table flex-100`,
  noDataText: 'No data found',
  minRows: 0,
  style: { height: '100%', borderRadius: '1rem', opacity: '1' }
};

export const depositSlipChequeTableConfig = {
  columns: [
    {
      Header: '',
      headerStyle: depositSlipTableHeaderStyle,
      accessor: 'checked',
      Cell: (row: Object) => renderActionButtons(row),
      style: { opacity: '1' }
    },
    {
      Header: 'CN',
      headerStyle: depositSlipTableHeaderStyle,
      accessor: 'cnote',
      Cell: (row: Object) => (
        <div>
          <div>
            <span className="text-wrap-ellipsis" title={row.original.cnote}>
              {row.original.cnote}
            </span>
            &nbsp;
            {row.original.additionalNumberOfCnotes ? (
              <span
                className="text-wrap-ellipsis"
                title={row.original.additionalNumberOfCnotes}
                style={{ color: '#dd712f' }}
              >
                (+{row.original.additionalNumberOfCnotes})
              </span>
            ) : null}
          </div>
          <div className="text-wrap-ellipsis" title={row.original.paymentMode}>
            {row.original.paymentMode}
          </div>
        </div>
      )
    },
    {
      Header: 'Instruments',
      headerStyle: depositSlipTableHeaderStyle,
      accessor: 'instrument',
      Cell: (row: Object) => (
        <div className="flex-100">
          <div className="text-wrap-ellipsis" title={row.original.instrument}>
            {row.original.instrument}
          </div>
          <div className="text-wrap-ellipsis" title={row.original.chequeNumber}>
            {row.original.chequeNumber}
          </div>
        </div>
      )
    },
    {
      Header: 'Bank Name/Branch',
      headerStyle: depositSlipTableHeaderStyle,
      accessor: 'bankName',
      Cell: (row: Object) => (
        <div className="text-wrap-ellipsis" title={row.original.bankName}>
          {row.original.bankName}
        </div>
      )
    },
    {
      Header: 'Cheque issued by',
      headerStyle: depositSlipTableHeaderStyle,
      accessor: 'chequeIssuedBy',
      Cell: (row: Object) => (
        <div className="text-wrap-ellipsis" title={row.original.chequeIssuedBy}>
          {row.original.chequeIssuedBy}
        </div>
      )
    },
    {
      Header: 'CollectedBy',
      headerStyle: depositSlipTableHeaderStyle,
      accessor: 'collectedBy',
      Cell: (row: Object) => (
        <div className="text-wrap-ellipsis" title={row.original.collectedBy}>
          {row.original.collectedBy}
        </div>
      )
    },
    {
      Header: 'Amount',
      headerStyle: depositSlipTableHeaderStyle,
      accessor: 'amount',
      Cell: (row: Object) => (
        <div className="text-wrap-ellipsis" title={row.original.amount}>
          {strUsables.indianCurrencySymol} {row.original.amount}
        </div>
      )
    }
  ],
  className: `deposit-slip-table flex-100`,
  noDataText: 'No data found',
  minRows: 10,
  style: { height: '100%', borderRadius: '1rem', opacity: '1' }
};
